import React, { useContext, useEffect, useState } from "react";
import Papa from "papaparse";
import UsersContext from "../Context/UsersContext";
import supabase from "./../Config/Supabase";
import { toast } from "react-toastify";
import {
  Loader,
  Upload,
  Database,
  FileText,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
const { v4: uuidv4 } = require("uuid");

const CsvReader = () => {
  const [data, setData] = useState([]);
  const { farmers } = useContext(UsersContext);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");

  // Helper function to chunk an array
  const chunkArray = (array, size) => {
    const result = [];
    for (let i = 0; i < array.length; i += size) {
      result.push(array.slice(i, i + size));
    }
    return result;
  };

  // Function to insert a batch with retry logic
  const insertBatchWithRetry = async (batch, supabase, maxRetries = 3) => {
    let attempt = 0;
    while (attempt < maxRetries) {
      const { error } = await supabase.from("payments").insert(batch).select();
      if (!error) {
        toast.success("Batch inserted successfully");
        return true; // Successful insert
      } else {
        console.error(`Attempt ${attempt + 1} failed. Retrying...`, error);
        attempt++;
        await new Promise((resolve) => setTimeout(resolve, 1000)); // Wait 1 second before retrying
      }
    }
    console.error("Batch failed after max retries:", batch);
    toast.error("Batch failed after max retries");
    return false; // Return false if all retries failed
  };

  // Function to insert data in batches with retry logic
  const insertDataInBatchesWithRetry = async (data, batchSize, supabase) => {
    const batches = chunkArray(data, batchSize);
    for (const batch of batches) {
      const success = await insertBatchWithRetry(batch, supabase);
      if (!success) {
        console.error(
          "Failed to insert batch after multiple retries. Stopping further inserts.",
        );
        toast.error(
          "Failed to insert batch after multiple retries. Stopping further inserts.",
        );
        break; // Stop further inserts if a batch fails
      }
    }
  };

  // Transforming CSV data into the format to insert
  const processCsvData = () => {
    const FarmerPayment = farmers?.map((fm) => {
      const paymentData = data?.filter((dt) => +dt?.Reference === fm?.id);
      return paymentData.map((dt) => ({
        id: uuidv4(),
        farmer_id: fm?.uuid,
        payment_method: "Mix by Yas",
        officer_id: fm?.creator_id,
        amount: parseInt(dt?.["Original Amount"].replace(/\D/g, ""), 10),
        created_at: new Date().toISOString(),
        synced_at: new Date().toISOString(),
      }));
    });

    return FarmerPayment?.flat(); // Flatten the array to get a single list of payment records
  };

  useEffect(() => {}, [data]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => {
          const fdata = result?.data?.filter(
            (fm) => fm?.["Synced With System"] === "",
          );
          setData(fdata);
        },
      });
    }
  };

  const handleInsertData = async () => {
    setLoading(true);
    setStatus("Inserting data...");

    try {
      const recordsToInsert = processCsvData();

      // Call the function to insert data in batches with retry logic
      await insertDataInBatchesWithRetry(recordsToInsert, 10, supabase); // Assuming supabase client is available

      setStatus("Data inserted successfully!");
    } catch (error) {
      setStatus("Failed to insert data after multiple retries.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-4 mx-auto max-w-7xl sm:p-6 lg:p-8 dark:bg-gray-800">
      {/* Header Section */}
      <div className="mb-6 sm:mb-8">
        <div className="flex gap-3 mb-2 sm:flex-row sm:items-center">
          <div className="p-2 rounded-lg sm:bg-white sm:dark:bg-gray-700">
            <FileText className="text-green-600 w-7 h-7 dark:text-green-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
              CSV Payment Data Reader
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Upload and process CSV files to import payment data into the
              system
            </p>
          </div>
        </div>
      </div>

      {/* Upload Section */}
      <div className="mb-6 sm:mb-8">
        <div className="p-6 text-center transition-colors duration-200 border-2 border-gray-300 border-dashed dark:border-gray-600 rounded-xl sm:p-8 hover:border-green-400 dark:hover:border-green-500 bg-gray-50/50 dark:bg-gray-800/50">
          <div className="flex flex-col items-center">
            <div className="p-3 mb-4 bg-white rounded-full sm:p-4 dark:bg-gray-700">
              <Upload className="w-6 h-6 text-gray-500 sm:w-8 sm:h-8 dark:text-gray-400" />
            </div>
            <h3 className="mb-2 text-base font-semibold text-gray-900 sm:text-lg dark:text-white">
              Upload CSV File
            </h3>
            <p className="max-w-md mb-4 text-sm text-gray-500 dark:text-gray-400">
              Select a CSV file to process payment data
            </p>
            <label className="relative cursor-pointer">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              <div className="px-4 py-2 text-sm font-medium text-white transition-colors duration-200 bg-green-600 rounded-lg cursor-pointer hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600 sm:px-6 sm:text-base">
                Choose CSV File
              </div>
            </label>
          </div>
        </div>
      </div>

      {data.length > 0 && (
        <>
          {/* Stats Section */}
          <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-2 lg:grid-cols-3 sm:gap-6 sm:mb-8">
            <div className="p-4 border border-blue-200 bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 sm:p-6 rounded-xl dark:border-blue-700/50">
              <div className="flex items-center">
                <div className="p-2 bg-blue-500 rounded-lg sm:p-3 dark:bg-blue-600">
                  <Database className="w-5 h-5 text-white sm:w-6 sm:h-6" />
                </div>
                <div className="ml-3 sm:ml-4">
                  <p className="text-xs font-medium text-blue-600 sm:text-sm dark:text-blue-400">
                    Records to Process
                  </p>
                  <p className="text-lg font-bold text-blue-900 sm:text-2xl dark:text-blue-100">
                    {data?.length}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-4 border border-green-200 bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/30 sm:p-6 rounded-xl dark:border-green-700/50">
              <div className="flex items-center">
                <div className="p-2 bg-green-500 rounded-lg sm:p-3 dark:bg-green-600">
                  <CheckCircle className="w-5 h-5 text-white sm:w-6 sm:h-6" />
                </div>
                <div className="ml-3 sm:ml-4">
                  <p className="text-xs font-medium text-green-600 sm:text-sm dark:text-green-400">
                    Status
                  </p>
                  <p className="text-sm font-semibold text-green-900 sm:text-lg dark:text-green-100">
                    Ready to Insert
                  </p>
                </div>
              </div>
            </div>

            <div className="p-4 border border-purple-200 bg-gradient-to-r from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/30 sm:p-6 rounded-xl dark:border-purple-700/50 sm:col-span-2 lg:col-span-1">
              <div className="flex items-center">
                <div className="p-2 bg-purple-500 rounded-lg sm:p-3 dark:bg-purple-600">
                  <FileText className="w-5 h-5 text-white sm:w-6 sm:h-6" />
                </div>
                <div className="ml-3 sm:ml-4">
                  <p className="text-xs font-medium text-purple-600 sm:text-sm dark:text-purple-400">
                    Batch Size
                  </p>
                  <p className="text-lg font-bold text-purple-900 sm:text-2xl dark:text-purple-100">
                    10
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Section */}
          <div className="mb-6 sm:mb-8">
            <div className="flex flex-col gap-4 p-4 border border-gray-200 md:justify-between md:flex-row sm:p-6 bg-gray-50 dark:bg-gray-900/30 rounded-xl dark:border-gray-700">
              <div className="text-center sm:text-left">
                <h3 className="mb-1 text-lg font-semibold text-gray-900 sm:text-xl dark:text-white">
                  Ready to Process
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Click the button below to start inserting the payment data
                </p>
              </div>
              <div className="flex justify-center sm:justify-start">
                <button
                  onClick={handleInsertData}
                  disabled={loading}
                  className={`px-6 sm:px-8 py-3 rounded-lg font-medium transition-all duration-200 transform hover:scale-105 text-sm sm:text-base w-full sm:w-auto ${
                    loading
                      ? "bg-gray-400 dark:bg-gray-600 cursor-not-allowed"
                      : "bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600 text-white shadow-lg hover:shadow-xl"
                  }`}
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <Loader className="w-4 h-4 [animation-duration:0.6s] animate-spin sm:w-5 sm:h-5" />
                      <span>Processing...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <Database className="w-4 h-4 sm:w-5 sm:h-5" />
                      <span>Insert Data</span>
                    </div>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Status Message */}
          {status && (
            <div
              className={`mb-6 sm:mb-8 p-4 rounded-lg border ${
                status.includes("successfully")
                  ? "bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-700/50 text-green-800 dark:text-green-200"
                  : status.includes("Failed")
                    ? "bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-700/50 text-red-800 dark:text-red-200"
                    : "bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-700/50 text-blue-800 dark:text-blue-200"
              }`}
            >
              <div className="flex items-center gap-2">
                {status.includes("successfully") ? (
                  <CheckCircle className="flex-shrink-0 w-4 h-4 sm:w-5 sm:h-5" />
                ) : status.includes("Failed") ? (
                  <AlertCircle className="flex-shrink-0 w-4 h-4 sm:w-5 sm:h-5" />
                ) : (
                  <Loader className="flex-shrink-0 w-4 [animation-duration:0.6s] h-4 sm:w-5 sm:h-5 animate-spin" />
                )}
                <span className="text-sm font-medium sm:text-base">
                  {status}
                </span>
              </div>
            </div>
          )}

          {/* Data Table */}
          <div className="overflow-hidden bg-white border border-gray-200 shadow-sm dark:bg-gray-900 rounded-xl dark:border-gray-700">
            <div className="px-4 py-4 border-b border-gray-200 sm:px-6 bg-gray-50 dark:bg-gray-800/50 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                CSV Data Preview
              </h3>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                Review the data before processing
              </p>
            </div>
            <div className="overflow-auto max-h-96">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 dark:bg-gray-900">
                  <tr>
                    {Object.keys(data[0]).map((header, index) => (
                      <th
                        key={index}
                        className="px-3 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b border-gray-200 sm:px-6 dark:text-gray-400 dark:border-gray-700 whitespace-nowrap"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-gray-700">
                  {data.map((row, index) => (
                    <tr
                      key={index}
                      className="transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    >
                      {Object.values(row).map((value, i) => (
                        <td
                          key={i}
                          className="px-3 py-4 text-sm text-gray-900 border-b border-gray-100 sm:px-6 whitespace-nowrap dark:text-gray-100 dark:border-gray-700"
                        >
                          <div className="max-w-xs truncate" title={value}>
                            {value}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CsvReader;
